import {FunctionComponent, useEffect, useState} from "react";
import {Skeleton, ASectionTitle} from "@atiautomacao/ati-ui-library";
import {FindExpectedDataReactQuery, FindPowerStationInformationReactQuery} from "./PowerStationInformationReactQuery";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Grid,
    TextField,
    Typography
} from "@mui/material";
import {endOfDay, isWithinInterval, startOfDay} from "date-fns";
import {LocalDateTimeFormatISO} from "../../../../Utils/DateFormatPatternUtils";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {truncateNumber} from "../../../../Utils/NumberUtil";
import { useAppSelector } from "../../../../Config/Hooks";
import { useSnackbar } from "notistack";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

interface RowInfo {
    name: string;
    value: any;
    unit?: string;
}

interface RowExpectedData {
    name: string;
    value: string | number;
    unit?: string;
    flexWrap?:boolean;
}

const PowerStationInformation: FunctionComponent<{powerStation: any}> = ({ powerStation }) => {

    const [dateTime, setDateTime] = useState<Date>(startOfDay(new Date()))
    const {data:expectedData, isLoading: expectedDataIsLoading} = FindExpectedDataReactQuery(dateTime, powerStation.id);
    const {data:dataInfo, isLoading: dataInfoIsLoading} = FindPowerStationInformationReactQuery(powerStation.id);
    const [rowInfo, setRowInfo] = useState<RowInfo[]>([])
    const [rowExpectedData, setRowExpectedData] = useState<RowExpectedData[]>([])
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const { enqueueSnackbar } = useSnackbar();
    
    const hasNoContent = (response:any) => {
        return response?.status === 204
    }

    const isOk = (response:any) => {
        return response?.status === 200
    }

    const dataIsValid = (data:any) => {
        return isOk(data) || hasNoContent(data)
    }



    const getDataInfo = (dataInfo: any) => {
        if(!dataIsValid(dataInfo)){
            return [   
                { name: 'Capacidade Instalada', value: '-', unit: 'MWp' },
                { name: 'Quantidade de Skid', value: '-'},
                { name: 'Início de operação', value: '-'},
                { name: 'Capacidade máxima AC', value: '-' },
            ]
        }

        let date
        if(dataInfo?.data.operationStartedAt !== undefined){
            date = new Date(dataInfo?.data.operationStartedAt)
        }
        const dataInfoList: RowInfo[] = [   
            { name: 'Sigla', value: !hasNoContent(dataInfo) && dataInfo.data.shortNameStation ? dataInfo.data.shortNameStation : '-', unit: '' },
            { name: 'Estado', value: !hasNoContent(dataInfo) && dataInfo.data.stateName ? dataInfo.data.stateName : '-', unit: '' },
            { name: 'Cidade', value: !hasNoContent(dataInfo) && dataInfo.data.cityName ? dataInfo.data.cityName : '-', unit: '' },
            { name: 'Quantidade de Skid', value: !hasNoContent(dataInfo) && dataInfo.data.numberOfSkid ? dataInfo.data.numberOfSkid : '-'},
            { name: 'CEP', value: !hasNoContent(dataInfo) && dataInfo.data.zipCode ? dataInfo.data.zipCode : '-'},
            { name: 'Início de operação', value: !hasNoContent(dataInfo) && date !== undefined ? date.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' }) : '-'},

            { name: 'Capacidade Instalada', value: !hasNoContent(dataInfo) && dataInfo.data.powerCapacityNominal ? dataInfo.data.powerCapacityNominal : '-', unit: 'kWp' },
            { name: 'Capacidade máxima AC', value: !hasNoContent(dataInfo) && dataInfo.data.maxPower ? dataInfo.data.maxPower : '-', unit: 'kW' },

            { name: 'Latitude', value: !hasNoContent(dataInfo) && dataInfo.data.latitude ? dataInfo.data.latitude : '-', unit: '' },
            { name: 'Longitude', value: !hasNoContent(dataInfo) && dataInfo.data.longitude ? dataInfo.data.longitude : '-', unit: '' },
            { name: 'Tracker/Fixo', value: !hasNoContent(dataInfo) && dataInfo.data.type ? dataInfo.data.type : '-', unit: '' },
            { name: 'Nome do Responsável', value: !hasNoContent(dataInfo) && dataInfo.data.adminName ? dataInfo.data.adminName : '-', unit: '' },
            { name: 'Email', value: !hasNoContent(dataInfo) && dataInfo.data.adminEmail ? dataInfo.data.adminEmail : '-', unit: '' },
            { name: 'Telefone', value: !hasNoContent(dataInfo) && dataInfo.data.adminPhone ? dataInfo.data.adminPhone : '-', unit: '' },

            
        ];
        return dataInfoList
    }

    const getExpectedData = (expectedData: any) => {
        if(!dataIsValid(expectedData)){return [
            { name: 'Energia Esperada', value: '-', unit: 'MWh', flexWrap: true},
            { name: 'Irradiação Esperada', value: '-', unit: 'Wh/m²', flexWrap: true },
            { name: 'Prod. Esperada', value: '-', unit: 'kWh/kWp', flexWrap: true },
            { name: 'PR Esperado', value: '-', unit: '%', flexWrap: false }
        ]}

        const expectedDataList: RowExpectedData[] = [
            { name: 'Energia Esperada', value: !hasNoContent(expectedData) && expectedData.data.expectedEnergy ? truncateNumber(expectedData.data.expectedEnergy,2) : '-', unit: 'MWh', flexWrap: true},
            { name: 'Irradiação Esperada', value: !hasNoContent(expectedData) && expectedData.data.expectedIrradiance ? truncateNumber(expectedData.data.expectedIrradiance,2) : '-', unit: 'Wh/m²', flexWrap: true },
            { name: 'Prod. Esperada', value: !hasNoContent(expectedData) && expectedData.data.expectedYield ? truncateNumber(expectedData.data.expectedYield,2): '-', unit: 'kWh/kWp', flexWrap: true },
            { name: 'PR Esperado', value: !hasNoContent(expectedData) && expectedData.data.expectedPerformanceRatio ? truncateNumber(expectedData.data.expectedPerformanceRatio * 100,2) : '-', unit: '%', flexWrap: false }
        ];
        return expectedDataList

    }

    useEffect(() => {
        setRowInfo(getDataInfo(null))
        setRowExpectedData(getExpectedData(null))
    }, []);

    useEffect(() => {
        if(dataIsValid(dataInfo)){
            const dataInfoList: RowInfo[] = getDataInfo(dataInfo);
            setRowInfo(dataInfoList)
            if(hasNoContent(dataInfo)){
                enqueueSnackbar(`As informações da usina não foram encontradas! `, { variant: "warning", autoHideDuration: 1000 });
            }
        }else{
            setRowInfo(getDataInfo(null))
        }
    }, [dataInfo]);

    useEffect(() => {
        if(dataIsValid(expectedData)){
            const expectedDataList: RowExpectedData[] = getExpectedData(expectedData);
            setRowExpectedData(expectedDataList)
            if(hasNoContent(expectedData)){
                enqueueSnackbar(`Os dados esperados da usina não foram encontrados! `, { variant: "warning", autoHideDuration: 1000 });
            }
        }else{
            setRowExpectedData(getExpectedData(null))
        }
    }, [expectedData]);

    return (
        <Grid container>
            <Grid item xs={12}>
                <ASectionTitle
                    styleContainer={{ padding: '0 0 16px 0' }}
                    titleLabel={`UFV ${powerStation.name}`}
                    contentChildren={
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Dia"
                                renderInput={(params) => <TextField sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', color: 'gray', height: '35px' } }} {...params} />}
                                value={dateTime}
                                inputFormat={"DD/MM/YYYY"}
                                onChange={(newValue) => {
                                    if (newValue && isWithinInterval(new Date(newValue), { start: new Date('1980-01-01'), end: endOfDay(new Date()) })) {
                                        setDateTime(startOfDay(new Date(LocalDateTimeFormatISO(newValue))));
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    }
                />

            </Grid>
            <Grid item xs={12}>


                <Accordion aria-label={`dashboard-station-info-${powerStation.name}`}>
                    <AccordionSummary
                        expandIcon={<ArrowDropDownIcon />}
                        aria-controls="panel1d-content"
                        id="panel1d-header"
                        sx={{ minHeight: '40px', '&.Mui-expanded': { minHeight: '40px', height: '40px' } }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Typography component="span">Informações da Usina</Typography>

                        </Box>

                    </AccordionSummary>
                    <AccordionDetails sx={{ borderTop: '1px solid rgba(0, 0, 0, .125)' }}>
                        {
                            (dataInfoIsLoading || expectedDataIsLoading) ? (

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: { xl: 1, lg: openSubMenu ? 4 : 10 }, mt: { xl: 2, lg: 0 }, mr: openSubMenu ? 3 : 0 }}>
                                    <Grid container spacing={{ xs: 1, md: 1 }} columnGap={0} columns={{ xs: 4, sm: 12, md: 12, xl: 12, lg: 12 }}>
                                        {Array.from({ length: 6 }).map((_, index) => (
                                            <Grid item xs={6} sm={4} md={4} lg={2} xl={2}>
                                                <Skeleton key={index.valueOf()} animation="wave" height={40} variant="text" width={'100%'} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: { xl: 1, lg: 1}, mt: { xl: 2, lg: 0 }, mr: openSubMenu ? 6 : 0 }}>
                                    <Grid container spacing={{ xs: 1, md: 1 }} columnGap={0} columns={{ xs: 12, sm: 12, md: 12, xl: 12, lg: 12 }}>
                                        {
                                            rowInfo.map((row, index) => {
                                                return (
                                                    <Grid aria-label={`${index}-dashboard-station-info-${powerStation.name}`}
                                                        item xs={6} sm={4} md={4} lg={2} xl={2} key={index.valueOf()}
                                                        color={'#000000'}
                                                    >
                                                        <Typography variant="body2" fontSize={'0.875rem'}>
                                                            {row.name}:
                                                        </Typography>
                                                        <Typography 
                                                            variant="h3" 
                                                            fontSize={'0.875rem'}
                                                            noWrap
                                                            sx={{ 
                                                                overflow: 'hidden', 
                                                                textOverflow: 'ellipsis',
                                                                maxWidth: '100%'
                                                            }}
                                                            title={`${row.value} ${row.unit || ''}`}
                                                        >
                                                            {row.value} {row.unit}
                                                        </Typography>
                                                    </Grid>
                                                )
                                            })
                                        }
                                        {
                                            rowExpectedData.map((row, index) => {
                                                return (
                                                    <Grid aria-label={`${index}-dashboard-station-expected-data`} item
                                                        xs={6} sm={4} md={4} xl={2} lg={2} key={index.valueOf()}>
                                                        <Typography variant="body2" fontSize={'0.875rem'}>
                                                            {row.name}:
                                                        </Typography>
                                                    <Typography 
                                                        variant="h3" 
                                                        fontSize={'0.875rem'}
                                                        noWrap
                                                        sx={{ 
                                                            overflow: 'hidden', 
                                                            textOverflow: 'ellipsis',
                                                            maxWidth: '100%'
                                                        }}
                                                        title={`${row.value} ${row.unit || ''}`}
                                                    >
                                                            {row.value} {row.unit}
                                                        </Typography>
                                                    </Grid>
                                                )
                                            })
                                        }
                                    </Grid>
                                </Box>
                            )}
                    </AccordionDetails>
                </Accordion>
            </Grid>
        </Grid>
    );
}
export default PowerStationInformation;